import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import { Send, Search, MessageCircle, FileText, Lock } from 'lucide-react';
import {
  fetchConversations, fetchMessages, loadOlderMessages, sendReply, sendTemplate, retryMessage,
  markThreadRead, markThreadAsLead, setActiveRecipient, receiveMessage, applyStatusUpdate,
} from '../../redux/metaChatSlice';
import MessageBubble from './chat/MessageBubble';
import TemplatePickerModal from './chat/TemplatePickerModal';
import { previewText } from './chat/chatUtils';

export default function MetaChatPage() {
  const dispatch = useDispatch();
  const {
    conversations, conversationsMeta, activeRecipient, messages, messagesMeta, session,
    loading, loadingMore, loadingOlder, messagesLoading, sending, error,
  } = useSelector((s) => s.metaChat);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [retryingId, setRetryingId] = useState(null);
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState('');
  const [searchParams] = useSearchParams();
  const socketRef = useRef(null);
  const bottomRef = useRef(null);
  const activeRecipientRef = useRef(activeRecipient);

  useEffect(() => { activeRecipientRef.current = activeRecipient; }, [activeRecipient]);

  // Deep-link from the notification bell — jump straight to the contact it's about.
  useEffect(() => {
    const recipient = searchParams.get('recipient');
    if (recipient) dispatch(setActiveRecipient(recipient));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const socket = io('/whatsapp', { auth: { token }, transports: ['websocket'] });
    socketRef.current = socket;

    socket.on('whatsapp:new_message', ({ message }) => dispatch(receiveMessage(message)));
    socket.on('whatsapp:status_update', (payload) => dispatch(applyStatusUpdate(payload)));

    return () => socket.disconnect();
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchConversations());
  }, [dispatch]);

  useEffect(() => {
    if (activeRecipient) {
      dispatch(fetchMessages(activeRecipient));
      dispatch(markThreadRead(activeRecipient));
    }
  }, [activeRecipient, dispatch]);

  const lastMessageId = messages[messages.length - 1]?._id;
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [lastMessageId]);

  const activeConv = conversations.find((c) => c.recipient === activeRecipient);
  const [markingLead, setMarkingLead] = useState(false);
  const handleMarkAsLead = async () => {
    if (!activeRecipient || markingLead) return;
    setMarkingLead(true);
    try {
      await dispatch(markThreadAsLead({ phone: activeRecipient, contactName: activeConv?.contactName })).unwrap();
    } finally {
      setMarkingLead(false);
    }
  };
  const filteredConversations = conversations.filter((c) =>
    !search || c.contactName?.toLowerCase().includes(search.toLowerCase()) || c.recipient.includes(search)
  );

  const handleSendTemplate = async (payload) => {
    const result = await dispatch(sendTemplate({ phone: activeRecipient, ...payload }));
    if (sendTemplate.fulfilled.match(result)) setPickerOpen(false);
    else dispatch(fetchMessages(activeRecipient)); // a failed send is stored — show it with its reason
  };

  const handleRetry = async (messageId) => {
    setRetryingId(messageId);
    try {
      await dispatch(retryMessage(messageId));
      dispatch(fetchMessages(activeRecipient));
    } finally {
      setRetryingId(null);
    }
  };

  const windowClosed = session && !session.open;
  const hasMoreConversations = conversationsMeta?.page && conversationsMeta.page * conversationsMeta.limit < conversationsMeta.total;

  const handleSend = async () => {
    if (!activeRecipient || !draft.trim()) return;
    const text = draft;
    setDraft('');
    await dispatch(sendReply({ phone: activeRecipient, text }));
  };

  return (
    <div className="flex flex-col lg:flex-row h-[calc(100vh-180px)] bg-[#F7F9FB] overflow-hidden border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
      {/* Left: Conversation List */}
      <div className="w-full lg:w-80 max-h-[40vh] lg:max-h-none flex-shrink-0 bg-white border-b lg:border-b-0 lg:border-r border-[#E5E5E5] flex flex-col">
        <div className="p-4 border-b border-[#E5E5E5]">
          <h1 className="text-lg font-semibold text-[#2E2E2E] mb-3">WhatsApp Chats</h1>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#706E6B]" size={14} />
            <input
              type="text"
              placeholder="Search contacts…"
              className="w-full pl-8 pr-3 py-2 text-sm border border-[#E5E5E5] rounded focus:outline-none focus:ring-2 focus:ring-[#25D366]"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading && conversations.length === 0 && <p className="text-center text-sm text-[#706E6B] mt-8">Loading…</p>}
          {!loading && filteredConversations.length === 0 && (
            <p className="text-center text-sm text-[#706E6B] mt-8">No conversations yet</p>
          )}
          {filteredConversations.map((conv) => {
            const isActive = conv.recipient === activeRecipient;
            const lm = conv.lastMessage;
            const preview = previewText(lm);
            return (
              <div
                key={conv.recipient}
                onClick={() => dispatch(setActiveRecipient(conv.recipient))}
                className={`flex items-start gap-3 p-4 cursor-pointer hover:bg-[#F7F9FB] border-b border-[#F3F2F2] ${isActive ? 'bg-[#E3F3E8]' : ''}`}
              >
                <div className="w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                  {(conv.contactName || '?')[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-[#2E2E2E] truncate">{conv.contactName}</span>
                    {lm?.createdAt && (
                      <span className="text-[11px] text-[#706E6B] flex-shrink-0 ml-2">
                        {new Date(lm.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#706E6B] truncate">{preview}</span>
                    {conv.unreadCount > 0 && (
                      <span className="ml-2 bg-[#25D366] text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 flex-shrink-0">
                        {conv.unreadCount}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {hasMoreConversations && (
            <button
              onClick={() => dispatch(fetchConversations({ page: conversationsMeta.page + 1 }))}
              disabled={loadingMore}
              className="w-full py-3 text-xs text-[#0176D3] hover:bg-[#F7F9FB] disabled:opacity-50"
            >
              {loadingMore ? 'Loading…' : 'Load more conversations'}
            </button>
          )}
        </div>
      </div>

      {/* Right: Thread */}
      <div className="flex-1 flex flex-col min-w-0">
        {!activeRecipient ? (
          <div className="flex-1 flex items-center justify-center text-[#706E6B]">
            <p className="text-sm">Select a conversation to view messages</p>
          </div>
        ) : (
          <>
            <div className="px-4 py-3 border-b border-[#E5E5E5] bg-white flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-[#25D366] flex items-center justify-center text-white text-sm font-bold">
                {(activeConv?.contactName || '?')[0].toUpperCase()}
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold text-[#2E2E2E]">{activeConv?.contactName || activeRecipient}</div>
                <div className="text-xs text-[#706E6B]">{activeRecipient}</div>
              </div>
              {activeConv?.leadId ? (
                <span className="text-xs bg-[#E3F3E8] text-[#036B3A] px-3 py-1 rounded-lg font-medium">Lead</span>
              ) : (
                <button
                  onClick={handleMarkAsLead}
                  disabled={markingLead}
                  className="flex items-center gap-1.5 text-xs bg-[#0176D3] hover:bg-[#014486] disabled:opacity-50 text-white px-3 py-1.5 rounded font-medium whitespace-nowrap"
                >
                  <MessageCircle size={13} />
                  {markingLead ? 'Marking…' : 'Mark as Lead'}
                </button>
              )}
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col bg-[#F7F9FB]">
              {messagesLoading && <p className="text-center text-sm text-[#706E6B] mt-8">Loading messages…</p>}
              {!messagesLoading && messages.length === 0 && (
                <p className="text-center text-sm text-[#706E6B] mt-8">No messages yet</p>
              )}
              {messagesMeta.hasMore && !messagesLoading && (
                <button
                  onClick={() => dispatch(loadOlderMessages({ phone: activeRecipient, page: messagesMeta.page + 1 }))}
                  disabled={loadingOlder}
                  className="self-center mb-3 text-xs text-[#0176D3] hover:underline disabled:opacity-50"
                >
                  {loadingOlder ? 'Loading…' : 'Load older messages'}
                </button>
              )}
              {messages.map((msg) => (
                <MessageBubble key={msg._id} msg={msg} onRetry={handleRetry} retrying={retryingId === msg._id} />
              ))}
              <div ref={bottomRef} />
            </div>

            {error && <div className="px-4 py-2 text-xs text-[#BA0517] bg-[#FDE7E9] border-t border-[#F6A3AB]">{error}</div>}

            {windowClosed && (
              <div className="px-4 py-2 text-xs text-[#8C4B02] bg-[#FFF4E5] border-t border-[#FFB75D] flex items-center gap-2">
                <Lock size={13} />
                {session.reason === 'never_messaged'
                  ? 'This contact has not messaged you — start the conversation with an approved template.'
                  : 'The 24-hour reply window has closed — send an approved template to continue.'}
              </div>
            )}
            {session?.open && session.expiresAt && (
              <div className="px-4 py-1 text-[11px] text-[#706E6B] bg-white border-t border-[#F3F2F2]">
                Free-form replies allowed until {new Date(session.expiresAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </div>
            )}
            <div className="p-3 border-t border-[#E5E5E5] bg-white flex items-center gap-2">
              <button
                onClick={() => setPickerOpen(true)}
                title="Send a template"
                className={`flex items-center gap-1.5 px-3 py-2 text-xs rounded border whitespace-nowrap ${windowClosed ? 'bg-[#25D366] text-white border-transparent hover:bg-[#1FAF56]' : 'border-[#D8DDE6] text-[#5C5C5C] hover:bg-[#F7F9FB]'}`}
              >
                <FileText size={14} /> Template
              </button>
              <input
                type="text"
                value={draft}
                disabled={windowClosed}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                placeholder={windowClosed ? 'Window closed — use a template' : 'Type a message…'}
                className="flex-1 px-3 py-2 text-sm border border-[#E5E5E5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#25D366] disabled:bg-[#F3F2F2]"
              />
              <button
                onClick={handleSend}
                disabled={sending || !draft.trim() || windowClosed}
                className="p-2.5 bg-[#25D366] hover:bg-[#1FAF56] text-white rounded disabled:opacity-50"
              >
                <Send size={16} />
              </button>
            </div>
            <TemplatePickerModal
              open={pickerOpen}
              onClose={() => setPickerOpen(false)}
              onSend={handleSendTemplate}
              sending={sending}
              contactName={activeConv?.contactName}
            />
          </>
        )}
      </div>
    </div>
  );
}
